// src/api/useChatApi.ts
import { useRef, useState } from "react";
import { ConversationRequest, InputMode, Message, ResponseLanguage } from "./models";
import { sendChat, synthesizeSpeech, RateLimitError, RateLimitInfo } from "./api";
import { getBrandConfig } from "../brands/brand-registry";
import { getTtsVoiceForResponse, normalizeResponseLanguage, TtsFeatureConfig } from "../services/ttsVoice";
import { beginAudioPrefetch, completeAudioSynthesis } from "../services/audioService";

export const useChatApi = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string>("");
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const turnSequenceRef = useRef(0);
  const brandConfig = getBrandConfig();
  const speechProvider = (brandConfig.features as { speechProvider?: 'openai' | 'abair' }).speechProvider || 'openai';
  const showVoiceSettings = 'showVoiceSettings' in brandConfig.features
    ? brandConfig.features.showVoiceSettings
    : true;
  const ttsFeatures = brandConfig.features as TtsFeatureConfig;

  const clearContext = () => {
    setMessages([]);
    setConversationId("");
    setRateLimitInfo(null);
    setIsRateLimited(false);
    turnSequenceRef.current = 0;
  };

  const isProcessing = () => {
    return messages.some(msg => msg.isThinking || msg.isStreaming);
  };

  const updateAssistantMessageByMessageId = (messageId: string | undefined, update: Partial<Message>) => {
    setMessages((prev) => {
      const copy = [...prev];

      let targetIndex = -1;
      if (messageId) {
        targetIndex = copy.findIndex(
          (msg) => msg.role === "assistant" && msg.messageId === messageId
        );
      }

      if (targetIndex === -1) {
        targetIndex = copy.map((m) => m.role).lastIndexOf("assistant");
      }

      if (targetIndex === -1) {
        return prev;
      }

      copy[targetIndex] = { ...copy[targetIndex], ...update };
      return copy;
    });
  };

  const updateLastAssistantMessage = (update: Partial<Message>) => {
    updateAssistantMessageByMessageId(undefined, update);
  };

  const updateLastAssistantMessagePlaceholder = (errorMsg: string) => {
    updateAssistantMessageByMessageId(undefined, {
      error: errorMsg,
      isThinking: false,
      isStreaming: false,
    });
  };

  const prefetchSpeechIfEnabled = (
    answerText: string | undefined,
    messageId: string | undefined,
    responseLanguage: ResponseLanguage | undefined,
  ) => {
    if (!answerText || !answerText.trim()) {
      return;
    }

    const configuredResponseVoice = getTtsVoiceForResponse(ttsFeatures, responseLanguage);
    if (!configuredResponseVoice) {
      updateAssistantMessageByMessageId(messageId, {
        voiceSynthesisStatus: undefined,
        prefetchedAudio: undefined,
        prefetchedVoice: undefined,
        prefetchedSpeed: undefined,
      });
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const shouldPrefetch = showVoiceSettings && localStorage.getItem("prefetchSpeech") === "true";
    if (!shouldPrefetch) {
      updateAssistantMessageByMessageId(messageId, {
        voiceSynthesisStatus: "idle",
        prefetchedAudio: undefined,
        prefetchedVoice: undefined,
        prefetchedSpeed: undefined,
      });
      return;
    }

    const selectedVoice = showVoiceSettings
      ? localStorage.getItem("selectedVoice") || configuredResponseVoice
      : configuredResponseVoice;

    const selectedSpeedKey = showVoiceSettings && typeof window !== "undefined"
      ? localStorage.getItem("speechSpeed") || "normal"
      : "normal";
    const selectedSpeedValue = selectedSpeedKey === "faster" ? 0.5 : 1;

    updateAssistantMessageByMessageId(messageId, {
      voiceSynthesisStatus: "pending",
      prefetchedAudio: undefined,
      prefetchedVoice: selectedVoice,
      prefetchedSpeed: selectedSpeedValue,
    });

    const synthesisRequest = beginAudioPrefetch();
    if (!synthesisRequest) {
      updateAssistantMessageByMessageId(messageId, { voiceSynthesisStatus: "idle" });
      return;
    }

    const { controller } = synthesisRequest;
    synthesizeSpeech(
      answerText,
      messageId,
      selectedVoice,
      selectedSpeedValue,
      speechProvider,
      controller.signal,
    )
      .then((response) => {
        if (controller.signal.aborted) {
          updateAssistantMessageByMessageId(messageId, { voiceSynthesisStatus: "idle" });
          return;
        }
        if (response?.audioContent) {
          updateAssistantMessageByMessageId(messageId, {
            voiceSynthesisStatus: "ready",
            prefetchedAudio: response.audioContent,
            prefetchedVoice: selectedVoice,
            prefetchedSpeed: selectedSpeedValue,
          });
        } else {
          throw new Error("Synthesis response missing audio content");
        }
      })
      .catch((error) => {
        if (controller.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
          updateAssistantMessageByMessageId(messageId, { voiceSynthesisStatus: "idle" });
          return;
        }
        console.error("Error prefetching speech:", error);
        updateAssistantMessageByMessageId(messageId, {
          voiceSynthesisStatus: "error",
          prefetchedSpeed: undefined,
        });
      })
      .finally(() => {
        completeAudioSynthesis(controller);
      });
  };

  const sendMessage = async (userQuery: string, isBriefResponse: boolean, inputMode: InputMode = "Text") => {
    // Generate temporary ID for UI tracking
    const tempId = Date.now().toString();
    turnSequenceRef.current += 1;
    const turnIndex = turnSequenceRef.current;
    const userUiMessageId = `turn-${turnIndex}-user`;
    const assistantUiMessageId = `turn-${turnIndex}-assistant`;
    
    // Add user message to UI (will be linked to turn via messageId when response arrives)
    setMessages((prev) => [
      ...prev,
      { uiMessageId: userUiMessageId, id: tempId, role: "user", englishText: userQuery },
    ]);

    // Add assistant placeholder (will receive real messageId from backend turn)
    setMessages((prev) => [
      ...prev,
      {
        uiMessageId: assistantUiMessageId,
        id: "",
        role: "assistant",
        englishText: "",
        citations: [],
        followUpQuestions: [],
        eventMessages: [],
        isThinking: true,
        isStreaming: true,
      },
    ]);

    const events: string[] = [];

    try {
      const payload: ConversationRequest = {
        id: conversationId,
        question: userQuery,
        isBriefResponse: isBriefResponse,
        source: "Web",
        input_mode: inputMode,
      };

      for await (const chunkObj of sendChat(payload)) {
        switch (chunkObj.type) {
          case "event":
            events.push(chunkObj.content);
            updateLastAssistantMessage({ eventMessages: [...events] });
            break;

          case "chunk":
            updateLastAssistantMessage({
              englishText: chunkObj.content["answer"],
              isThinking: false,
            });
            break;

          case "final":
            const responseLanguage = normalizeResponseLanguage(chunkObj.responseLanguage);
            if (chunkObj.conversationId) {
              setConversationId(chunkObj.conversationId);
            }
            updateLastAssistantMessage({
              id: chunkObj.conversationId || "",
              messageId: chunkObj.messageId,
              englishText: chunkObj.answer ?? chunkObj.content, // Now contains response in target language
              citations: chunkObj.citations ?? [],
              citationTitles: chunkObj.citationTitles ?? {},
              followUpQuestions: chunkObj.followUpQuestions ?? [], // Now contains followups in target language
              responseLanguage,
              eventMessages: [...events],
              isThinking: false,
              isStreaming: false,
              voiceSynthesisStatus: getTtsVoiceForResponse(ttsFeatures, responseLanguage) ? "idle" : undefined,
              prefetchedAudio: undefined,
              prefetchedVoice: undefined,
              prefetchedSpeed: undefined,
            });
            prefetchSpeechIfEnabled(
              chunkObj.answer ?? chunkObj.content,
              chunkObj.messageId,
              responseLanguage,
            );
            break;

          case "error":
            updateLastAssistantMessage({
              error: chunkObj.content,
              isThinking: false,
              isStreaming: false,
            });
            break;

          default:
            console.warn("Unknown chunk type:", chunkObj.type);
        }
      }
    } catch (err: any) {
      console.error("Error sending message:", err);
      
      // Handle rate limiting
      if (err instanceof RateLimitError) {
        setRateLimitInfo(err.rateLimitInfo);
        setIsRateLimited(true);
        updateLastAssistantMessagePlaceholder(err.message);
        return;
      }
      
      updateLastAssistantMessagePlaceholder("Failed to fetch response.");
    }
  };

  const handleRateLimitRetry = () => {
    setIsRateLimited(false);
    setRateLimitInfo(null);
  };

  const handleRateLimitError = (error: RateLimitError) => {
    setRateLimitInfo(error.rateLimitInfo);
    setIsRateLimited(true);
  };

  return { 
    messages, 
    sendMessage, 
    clearContext, 
    conversationId, 
    isProcessing,
    rateLimitInfo,
    isRateLimited,
    handleRateLimitRetry,
    handleRateLimitError
  };
};
